import { getSupabaseClient } from '../../../../core/supabase/client.js';
import type { ContactDto } from '../dtos/ContactDto.js';

const FIELDS = 'id, full_name, id_number, phone, is_phone_verify, otp_provider, otp_code, bank_accounts, photos, org_id, project_id, created_by, created_at, updated_at';

export class ContactsRemoteDataSource {
  private get supabase() { return getSupabaseClient() as any; }

  async list(options: { orgId: string; projectId?: string; search?: string; limit?: number; offset?: number }): Promise<{ data: ContactDto[]; total: number }> {
    let query = this.supabase.from('contacts').select(FIELDS, { count: 'exact' })
      .eq('org_id', options.orgId)
      .order('full_name', { ascending: true });
    if (options.projectId) query = query.eq('project_id', options.projectId);
    if (options.search) query = query.ilike('full_name', `%${options.search}%`);
    if (typeof options.limit === 'number' && typeof options.offset === 'number') {
      query = query.range(options.offset, options.offset + options.limit - 1);
    } else if (typeof options.limit === 'number') {
      query = query.limit(options.limit);
    }
    const { data, error, count } = await query;
    if (error) throw new Error(error.message);
    return { data: data ?? [], total: count ?? 0 };
  }

  async getById(id: string): Promise<ContactDto | null> {
    const { data, error } = await this.supabase.from('contacts').select(FIELDS).eq('id', id).maybeSingle();
    if (error) throw new Error(error.message);
    return data ?? null;
  }

  async create(payload: Omit<ContactDto, 'id' | 'created_at' | 'updated_at' | 'created_by'>): Promise<ContactDto> {
    const { data: { user } } = await this.supabase.auth.getUser();
    const { data, error } = await this.supabase.from('contacts')
      .insert({ ...payload, created_by: user?.id })
      .select(FIELDS).single();
    if (error) throw new Error(error.message);
    return data;
  }

  async update(id: string, payload: Partial<ContactDto>): Promise<ContactDto> {
    const { data, error } = await this.supabase.from('contacts').update(payload).eq('id', id).select(FIELDS).single();
    if (error) throw new Error(error.message);
    return data;
  }

  async delete(id: string): Promise<void> {
    const { error } = await this.supabase.from('contacts').delete().eq('id', id);
    if (error) throw new Error(error.message);
  }
}
